import React from "react";
import { Fade } from "../../components/reveal/Fade";
import Button from "../../components/button/Button";
import { greeting } from "../../portfolio";
import "./ExperienceRail.css";

// Frise « rail » (2/3) : dates à gauche, ligne fine avec un point par poste,
// cartes de même largeur ; à droite, stack et actions fixes au défilement.

const withAlpha = (hex, alpha) => {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i.exec(hex || "");
  if (!m) return hex;
  const [r, g, b] = m.slice(1).map((h) => parseInt(h, 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

function RailItem({ experience, theme, muted }) {
  const accent = theme.imageHighlight;
  const highlights = experience.highlights || [];
  const tags = experience.tags || [];
  const company = experience.company_url ? (
    <a
      className="rail-company"
      href={experience.company_url}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: accent }}
    >
      {experience.company}
      <span aria-hidden="true"> ↗</span>
    </a>
  ) : (
    <span className="rail-company" style={{ color: theme.secondaryText }}>
      {experience.company}
    </span>
  );

  return (
    <Fade bottom duration={1000} distance="24px">
      <li className="rail-item">
        <div className="rail-date">
          <span className="rail-period" style={{ color: theme.text }}>
            {experience.period || experience.duration}
          </span>
          {experience.location && (
            <span className="rail-place" style={{ color: theme.secondaryText }}>
              {experience.location}
            </span>
          )}
        </div>
        <div className="rail-track" aria-hidden="true">
          <span
            className="rail-dot"
            style={{
              borderColor: muted ? theme.secondaryText : accent,
              backgroundColor: experience.current ? accent : theme.body,
              boxShadow: experience.current
                ? `0 0 0 4px ${withAlpha(accent, 0.18)}`
                : "none",
            }}
          />
        </div>
        <article
          className="rail-card"
          style={{ backgroundColor: theme.cardBg || "#ffffff" }}
        >
          <header className="rail-card-head">
            <img
              className="rail-logo"
              src={require(`../../assets/images/${experience.logo_path}`)}
              alt=""
            />
            <div className="rail-card-titles">
              <h3 className="rail-role" style={{ color: theme.text }}>
                {experience.title}
              </h3>
              {company}
            </div>
            {experience.current && <span className="rail-badge">En poste</span>}
          </header>
          {highlights.length > 0 ? (
            <ul className="rail-points" style={{ color: theme.secondaryText }}>
              {highlights.map((point, i) => (
                <li key={i}>{point}</li>
              ))}
            </ul>
          ) : (
            experience.description && (
              <p className="rail-text" style={{ color: theme.secondaryText }}>
                {experience.description}
              </p>
            )
          )}
          {tags.length > 0 && (
            <div className="rail-tags">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="rail-tag"
                  style={{
                    color: theme.text,
                    backgroundColor: withAlpha(accent, 0.1),
                    borderColor: withAlpha(accent, 0.22),
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </article>
      </li>
    </Fade>
  );
}

export default function ExperienceRail({ sections, theme }) {
  // Stack = toutes les technos des postes, dans l'ordre d'apparition, sans doublon
  const stack = [
    ...new Set(
      sections
        .filter((section) => section.work)
        .flatMap((section) => section.experiences)
        .flatMap((experience) => experience.tags || [])
    ),
  ];

  return (
    <div className="rail-layout">
      <div className="rail" style={{ "--rail-line": theme.headerColor }}>
        {sections.map((section, s) => (
          <section key={section.title} className="rail-section">
            {s > 0 && (
              <h2 className="rail-section-title" style={{ color: theme.text }}>
                {section.title}
              </h2>
            )}
            <ol className="rail-list">
              {section.experiences.map((experience) => (
                <RailItem
                  key={experience.company + experience.title}
                  experience={experience}
                  theme={theme}
                  muted={!section.work}
                />
              ))}
            </ol>
          </section>
        ))}
      </div>
      {/* Colonne de droite, fixe au défilement (masquée sous 1000px) */}
      <aside className="rail-side">
        {stack.length > 0 && (
          <div
            className="rail-side-box"
            style={{ backgroundColor: theme.cardBg || "#ffffff" }}
          >
            <h2
              className="rail-side-title"
              style={{ color: theme.secondaryText }}
            >
              Stack
            </h2>
            <div className="rail-tags">
              {stack.map((tag) => (
                <span
                  key={tag}
                  className="rail-tag"
                  style={{
                    color: theme.text,
                    backgroundColor: withAlpha(theme.imageHighlight, 0.1),
                    borderColor: withAlpha(theme.imageHighlight, 0.22),
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
        <div className="rail-side-actions">
          <Button
            text="Télécharger mon CV"
            newTab={true}
            href={greeting.resumeLink}
            theme={theme}
          />
          <Button
            text="Me contacter"
            href="#/contact"
            theme={theme}
            variant="outline"
          />
        </div>
      </aside>
    </div>
  );
}
